import type moment from 'moment'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import Hr from 'components/Common/Hr'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import SolidBuildingIcon from 'LuxKit/Icons/solid/SolidBuildingIcon'
import FlexibleCancellationCharterFlightFees from './FlexibleCancellationCharterFlightFees'
import { TimelineNode, TimelineEndNode } from './CancellationPolicyTimelineComponents'
import {
  formatFlexibleCancellationPolicyDate,
  getFlexibleCancellationDateChangeAllowance,
  getFlexibleCancellationTimelinePolicyDates,
} from './flexibleCancellationPolicyTimelineUtils'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'

interface Props {
  today: moment.Moment
  checkInDate: string | moment.Moment
  startBasedOnToday: moment.Moment
  startBasedOnCheckIn: moment.Moment
  cancellationAfterChangeOfMindPeriod: boolean
  /** Whether this is a charter bundle (flights + hotel). Affects terminology and shows charter-specific fees. */
  isCharter?: boolean
  /** Offer-configured date change allowance: 'Unlimited' or a numeric string like '2'. */
  numberOfDateChangesAllowed?: string
}

function FlexibleCancellationPolicyModalWithDates(props: Props) {
  const {
    today,
    checkInDate,
    startBasedOnToday,
    startBasedOnCheckIn,
    cancellationAfterChangeOfMindPeriod,
    isCharter,
    numberOfDateChangesAllowed,
  } = props

  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const fullRefundColour = useRebrandSuccessDarkColour()

  const {
    fullRefundDate,
    partialRefundDate,
    partialRefundDeadline,
    noRefundDate,
  } = getFlexibleCancellationTimelinePolicyDates({
    startBasedOnToday,
    startBasedOnCheckIn,
  })

  const formatDateForPolicy = useCallback((date: string | moment.Moment) => formatFlexibleCancellationPolicyDate(
    date,
    today,
    intlDateFormatter,
  ), [intlDateFormatter, today])

  const checkInDateLabel = isCharter
    ? intl.formatMessage(
        {
          id: 'jdUCuaxcQF-NeXnLagHX',
          defaultMessage: '{date} · Departure',
        },
        { date: formatDateForPolicy(checkInDate) },
      )
    : intl.formatMessage(
        {
          id: 'd59NymZ4PuZiqmggDhvS',
          defaultMessage: '{date} · Check-in',
        },
        { date: formatDateForPolicy(checkInDate) },
      )

  const dateChangeAllowance = useMemo(
    () => getFlexibleCancellationDateChangeAllowance(numberOfDateChangesAllowed, isCharter),
    [isCharter, numberOfDateChangesAllowed],
  )

  const dateChangeWindow = useMemo(() => ({
    startDate: formatDateForPolicy(fullRefundDate),
    endDate: formatDateForPolicy(partialRefundDeadline),
  }), [formatDateForPolicy, fullRefundDate, partialRefundDeadline])

  const isPartialRefundToday = partialRefundDate.isSame(today, 'day')

  const upToDateLabel = intl.formatMessage(
    {
      id: 'l3pEXYwwkXfvG_HOiItm',
      defaultMessage: 'Up to {date}',
    },
    { date: formatDateForPolicy(partialRefundDate) },
  )

  const upToDateTodayLabel = intl.formatMessage(
    {
      id: '3WWQUkwCPrh4pVS0MjbK',
      defaultMessage: 'Up to {date} · Today',
    },
    { date: formatDateForPolicy(partialRefundDate) },
  )

  const partialRefundDateTodayLabel = intl.formatMessage(
    {
      id: 'J2kDJu22K6pdAWrxdqQV',
      defaultMessage: '{date} · Today',
    },
    { date: formatDateForPolicy(partialRefundDate) },
  )

  return (
    <>
      <Group direction="vertical">
        <TimelineNode
          label={isPartialRefundToday ? upToDateTodayLabel : upToDateLabel}
        >
          <BodyText variant="medium" weight="bold" colour={fullRefundColour}>
            {intl.formatMessage({
              id: 'IPvnv3wz6i5O1pItlNwB',
              defaultMessage: 'Full refund',
            })}
          </BodyText>
          <BodyText variant="medium" as="p">
            {isCharter && intl.formatMessage({
              id: 'jhe_gCUlHNGZxsCU6At0',
              defaultMessage: 'Cancel before {date}, 11:59 pm to get full refund to your original payment method or credit.',
            }, { date: formatDateForPolicy(fullRefundDate) })}
            {!isCharter && intl.formatMessage({
              id: 'YbSKjtELmIvBoE3NWzzr',
              defaultMessage: 'Cancel before {date}, 11:59 pm to get full refund of accommodation purchase price to your original payment method or credit.',
            }, { date: formatDateForPolicy(fullRefundDate) })}
          </BodyText>
        </TimelineNode>

        {cancellationAfterChangeOfMindPeriod && !isCharter && (
          <TimelineNode
            label={isPartialRefundToday ? partialRefundDateTodayLabel : formatDateForPolicy(partialRefundDate)}
          >
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'BkK38vGr7Mr942F0vUUA',
                defaultMessage: 'Full refund to credit',
              })}
            </BodyText>
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'NMl5qQRos3BLleHcfVU-',
                defaultMessage: 'Cancel before {date}, 11:59 pm to receive 100% refund to credit.',
              }, { date: formatDateForPolicy(partialRefundDeadline) })}
            </BodyText>
          </TimelineNode>
        )}

        {cancellationAfterChangeOfMindPeriod && isCharter && (
          <TimelineNode
            label={isPartialRefundToday ? partialRefundDateTodayLabel : formatDateForPolicy(partialRefundDate)}
          >
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'KsmgOLy4pOps0C4ljO4T',
                defaultMessage: 'Partial refund',
              })}
            </BodyText>
            <Group direction="vertical" gap={8}>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'UCWntp5VmYKW3qLQ9ovG',
                  defaultMessage: 'Cancel before {date}, 11:59 pm to get a partial refund to your original payment method or credit. Cancellation fees apply:',
                }, { date: formatDateForPolicy(partialRefundDeadline) })}
              </BodyText>
              <FlexibleCancellationCharterFlightFees />
            </Group>
          </TimelineNode>
        )}

        <TimelineNode label={formatDateForPolicy(noRefundDate)}>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'yAazKuRHWgNT7wkPi4c9',
              defaultMessage: 'No refund',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'ah8w_Ec4UjW_D49bKyOc',
              defaultMessage: 'Cancellations after {date}, 11:59 pm are non-refundable.',
            }, { date: formatDateForPolicy(partialRefundDeadline) })}
          </BodyText>
        </TimelineNode>

        <TimelineEndNode label={checkInDateLabel} icon={!isCharter ? SolidBuildingIcon : undefined} />
      </Group>

      <Hr size="md" />

      <div>
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'zOgZxgWqO_0Kba00Iaqk',
            defaultMessage: 'Date changes',
          })}
        </BodyText>
        <BodyText variant="medium">
          {dateChangeAllowance.kind === 'unlimited' && intl.formatMessage({
            id: 'eTdPkOsXQYv_WWnmOfTl',
            defaultMessage: 'Unlimited changes permitted between {startDate} and {endDate}. Subject to availability and applicable surcharges (peak periods or property-specific).',
          }, dateChangeWindow)}
          {dateChangeAllowance.kind === 'limited' && dateChangeAllowance.count === 1 && intl.formatMessage({
            id: '7faDfrrNU0w8A0OdSY9Z',
            defaultMessage: 'One change permitted between {startDate} and {endDate}. Subject to availability and applicable surcharges (peak periods or property-specific).',
          }, dateChangeWindow)}
          {dateChangeAllowance.kind === 'limited' && dateChangeAllowance.count > 1 && intl.formatMessage({
            id: 'Jd4m8kbiUW3V-7OGOMq8',
            defaultMessage: '{count, plural, one {One change} other {# changes}} permitted between {startDate} and {endDate}. Subject to availability and applicable surcharges (peak periods or property-specific).',
          }, { ...dateChangeWindow, count: dateChangeAllowance.count })}
          {dateChangeAllowance.kind === 'none' && intl.formatMessage({
            id: 'q5IDmM7KYiN5jzfti-vk',
            defaultMessage: 'Date changes are not permitted for this offer.',
          })}
          {dateChangeAllowance.kind === 'unknown' && intl.formatMessage({
            id: 'swrejTr_Tm7rw0BpmU0T',
            defaultMessage: 'Changes may be permitted between {startDate} and {endDate}, as per the offer terms and conditions. Subject to availability and applicable surcharges (peak periods or property-specific).',
          }, dateChangeWindow)}
        </BodyText>
      </div>
    </>
  )
}

export default FlexibleCancellationPolicyModalWithDates
